import { Button, ButtonGroup, IconButton, Stack } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { HiX } from 'react-icons/hi';

import { DataTableFilter, Loosen } from '../utils/typings';
import { BooleanFilterPopover } from './Filters/BooleanFilterPopover';
import { CheckboxFilterPopover } from './Filters/CheckboxFilterPopover';
import { DateFilterPopover } from './Filters/DateFilterPopover';
import { OperatorFilterPopover } from './Filters/OperatorFilterPopover';
import { RadioFilterPopover } from './Filters/RadioFilterPopover';
import { SimpleRangeSlider } from './Filters/SimpleRangeSlider';
import { StringFilterPopover } from './Filters/StringFilterPopover';

type Props<T extends DataTableFilter[]> = {
  filters: T;
  setFilters: (arg: Loosen<T> | ((type: Loosen<T>) => Loosen<T>)) => void;
  showOperator?: boolean;
  onClear?: () => void;
  isClearable?: boolean;
};

export const DataTableFilters = <T extends DataTableFilter[]>({
  filters,
  setFilters,
  onClear,
  isClearable = true,
}: Props<T>) => {
  const handleChange = (index: number) => (value: any) => {
    setFilters((prevFilters: T) => {
      const updatedFilters = [...prevFilters];
      const filter = updatedFilters[index];

      filter.value = value;

      setFilters(updatedFilters);
      return updatedFilters;
    });
  };

  const handleChangeOperator = (index: number) => (operator: DataTableFilter['operator']) => {
    setFilters((prevFilters: T) => {
      const updatedFilters = [...prevFilters];
      const filter = updatedFilters[index];
      filter.operator = operator;
      return updatedFilters;
    });
  };

  const handleDelete = (index: number) => () => {
    setFilters((prevFilters: T) => {
      const updatedFilters = [...prevFilters];
      updatedFilters.splice(index, 1);
      return updatedFilters;
    });
  };

  const renderFilter = (filter: DataTableFilter, index: number) => {
    const rangeSliderFilter = filter as Extract<DataTableFilter, { inputType: 'date-range-slider' }>;

    return (
      <ButtonGroup key={filter?.name} size="sm" colorScheme="gray" isAttached variant="outline" zIndex="9">
        <Show if={!filter?.hideLabel}>
          <Button size="sm" pointerEvents="none">
            {filter?.label}
          </Button>
        </Show>
        <Show if={!!filter?.showOperator}>
          <OperatorFilterPopover
            operator={filter?.type}
            value={filter?.operator}
            onChange={handleChangeOperator(index)}
          />
        </Show>
        <Show if={filter?.inputType === 'string'}>
          <StringFilterPopover onChange={handleChange(index)} value={filter?.value as string} />
        </Show>
        <Show if={filter?.inputType === 'boolean'}>
          <BooleanFilterPopover
            label={filter?.value ? 'true' : 'false'}
            value={filter?.value as boolean}
            onChange={handleChange(index)}
          />
        </Show>
        <Show if={filter?.inputType === 'radio'}>
          <RadioFilterPopover
            value={filter?.value as string}
            options={filter?.options}
            onChange={handleChange(index)}
          />
        </Show>
        <Show if={filter?.inputType === 'checkbox'}>
          <CheckboxFilterPopover
            value={filter?.value as string[]}
            options={filter?.options}
            onChange={handleChange(index)}
          />
        </Show>
        <Show if={filter?.inputType === 'date'}>
          <DateFilterPopover value={filter?.value} onChange={handleChange(index)} />
        </Show>
        <Show if={isClearable}>
          <IconButton aria-label="Clear filter" icon={<HiX />} onClick={handleDelete(index)} />
        </Show>
        <Show if={filter?.inputType === 'date-range-slider'}>
          <SimpleRangeSlider
            label={filter?.label}
            value={rangeSliderFilter?.value}
            onChange={handleChange(index)}
            minValue={rangeSliderFilter?.minValue}
            maxValue={rangeSliderFilter?.maxValue}
            toIndex={rangeSliderFilter?.toIndex}
            fromIndex={rangeSliderFilter?.fromIndex}
            formatValue={rangeSliderFilter?.formatValue}
            isEqual={rangeSliderFilter?.isEqual}
            anyLabel={rangeSliderFilter?.anyLabel}
          />
        </Show>
      </ButtonGroup>
    );
  };

  return (
    <Stack direction={{ xl: 'row', base: 'column' }} align="center" spacing="4" wrap="wrap">
      {filters?.map(renderFilter)}
      <Show if={isClearable}>
        <Button size="sm" colorScheme="gray" variant="ghost" onClick={onClear}>
          Clear all
        </Button>
      </Show>
    </Stack>
  );
};
